import Banner from '@/components/UI/Banner';
import { _isNotBlank, _notNil } from '@/littledash';
import type { SendInputFailure } from '@/model/Agent.model';
import { useMemo, type FC } from 'react';
import { plural } from '@/components/Agents/SendValidator/utils/sendText';
import { INPUT_FAILURE_TEXT } from '@/components/Agents/SendValidator/utils/sendInputFailureText';
import styles from './sendResults.module.scss';

interface SendInputFailuresProps {
  failures: Array<SendInputFailure>;
  testId?: string;
}

/** The failures for one file, or for the upload as a whole. */
interface FailureGroup {
  key: string;
  label: string;
  failures: Array<SendInputFailure>;
}

/** A bundle-level failure belongs to no one file. */
const UPLOAD_AS_A_WHOLE = 'The upload as a whole';

const locationOf = (failure: SendInputFailure): string => {
  const parts: Array<string> = [];
  if (_notNil(failure.row_number)) {
    parts.push(`Row ${failure.row_number}`);
  }
  if (_notNil(failure.line_number)) {
    parts.push(`line ${failure.line_number}`);
  }
  if (_notNil(failure.column_name)) {
    parts.push(`column '${failure.column_name}'`);
  } else if (_notNil(failure.column_position)) {
    parts.push(`column ${failure.column_position}`);
  }
  return parts.join(', ');
};

/**
 * Groups failures by the file they belong to, in the server's order, with any that belong to the
 * upload as a whole first: those stop every file, so they are the first thing to fix.
 */
const groupsOf = (failures: ReadonlyArray<SendInputFailure>): Array<FailureGroup> => {
  const groups = failures.reduce<Map<string, FailureGroup>>((byFile, failure) => {
    // A file is known by its upload id, or by its name when the failure carries no id. Only a failure
    // with neither belongs to the upload as a whole.
    const key = failure.attachment_api_id ?? (_notNil(failure.filename) ? `file:${failure.filename}` : 'upload');
    const group = byFile.get(key) ?? { key, label: failure.filename ?? UPLOAD_AS_A_WHOLE, failures: [] };
    group.failures.push(failure);
    byFile.set(key, group);
    return byFile;
  }, new Map());
  return [...groups.values()].sort((a, b) => Number(b.key === 'upload') - Number(a.key === 'upload'));
};

/**
 * Why a SEND run stopped on files it could not read: a terminal state, not a result. It says plainly
 * that nothing was checked, groups the problems by file with the upload-wide ones first, and says
 * what to do about each, so a stopped run can never read as one that found nothing wrong. Shared by
 * the confirm step and the results view, which is where the server can stop a run.
 */
const SendInputFailures: FC<SendInputFailuresProps> = ({ failures, testId = 'send-input-failures' }) => {
  const groups = useMemo(() => groupsOf(failures), [failures]);
  return (
    <div data-test-component="SendInputFailures" data-testid={testId}>
      <Banner critical dismiss={false} testId={`${testId}__banner`}>
        <p className="ma0 fw5" data-test-element="headline">
          Nothing was checked: {plural(failures.length, 'problem', 'problems')} to fix before validation can run
        </p>
      </Banner>
      <p className="dark-gray f6 mv3" data-test-element="summary">
        The run stopped because the upload could not be read. Nothing was checked against SEND, so these are not SEND
        findings: fix the problems below and upload the files again.
      </p>
      <ul className="list pl0 ma0" data-test-element="failure-groups">
        {groups.map((group) => (
          <li
            key={group.key}
            className={styles.failureGroup}
            data-test-element="failure-group"
            data-test-key={group.key}
          >
            <h4 className={styles.failureGroupTitle} data-test-element="failure-file">
              {group.label}
            </h4>
            <ul className="list pl0 ma0">
              {group.failures.map((failure) => {
                const location = locationOf(failure);
                return (
                  <li
                    key={`${failure.kind}-${failure.row_number ?? ''}-${failure.line_number ?? ''}-${failure.column_name ?? failure.column_position ?? ''}`}
                    className="pv2 f6"
                    data-test-element="failure"
                    data-test-key={failure.attachment_api_id ?? 'upload'}
                  >
                    {_isNotBlank(location) && (
                      <div className="mid-gray" data-test-element="failure-location">
                        {location}
                      </div>
                    )}
                    <div className="dark-gray" data-test-element="failure-message">
                      {failure.message}
                    </div>
                    {_notNil(failure.configured_limit) && (
                      <div className="mid-gray" data-test-element="failure-limit">
                        Configured limit: {failure.configured_limit.toLocaleString('en-US')}
                      </div>
                    )}
                    <div className="near-black mt1" data-test-element="failure-fix">
                      <span className="fw6">What to do: </span>
                      {INPUT_FAILURE_TEXT[failure.kind] ?? 'Export the file again and upload it.'}
                    </div>
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default SendInputFailures;
